import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {MAX_METHOD_NAME_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import {MethodUpdater} from 'procedures/design/ProcedureEditor';
import {validateMethodName} from 'procedures/design/validation/methodValidationUtils';
import {Method} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, RED} from 'ui-library/Colors';
import FieldError from 'ui-library/FieldError';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {ElementKeyboardEvent} from 'util/Types';

import {normalizeNameForUniqueness} from '../flowchartManagementUtils';
import {getMethodNamePlaceholder} from '../procedureMethodNodeCommonConsts';

export interface MethodMetadataEditorProps {
  selectedMethod: Method;
  methodNamesInUse: Array<string>;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  isProcedureReadOnly: boolean;
}

interface MethodNameFieldProps {
  initialName: string;
  disallowedNames: Array<string>;
  onNameChange: (newName: string) => string;
  isDisabled: boolean;
}

export function MethodNameField(props: MethodNameFieldProps): JSX.Element {
  const [name, setName] = useState(props.initialName);
  const {procedureType} = ProcedurePageContext.useContext();
  const error = validateMethodName(name, props.disallowedNames, procedureType);

  const submit = (): void => {
    const finalName = error ? props.initialName : name;
    setName(props.onNameChange(finalName));
  };

  const submitOnEnter = (e: ElementKeyboardEvent): void => {
    if (e.key === 'Enter') {
      submit();
    }
  };

  return (
    <>
      <input
        className={css(styles.methodNameField, error != null && styles.methodNameFieldError)}
        type="text"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
        onBlur={submit}
        onKeyPress={submitOnEnter}
        autoFocus={true}
        disabled={props.isDisabled}
        maxLength={MAX_METHOD_NAME_LENGTH}
      />
      {error && <FieldError message={error} />}
    </>
  );
}

export default function MethodMetadataEditor(props: MethodMetadataEditorProps): JSX.Element {
  const {procedureType} = ProcedurePageContext.useContext();

  return (
    <div
      className={css(styles.metadataTabContent)}
      {...getTestProps({component: 'MethodMetadataEditor', element: 'root'})}
    >
      <FieldLabel isRequired={true} label="Name" />
      <MethodNameField
        initialName={props.selectedMethod.name}
        disallowedNames={props.methodNamesInUse}
        onNameChange={(newName: string) => {
          const uniqueMethodName = normalizeNameForUniqueness(
            newName,
            props.methodNamesInUse,
            getMethodNamePlaceholder(procedureType)
          );
          props.onChangeMethod(props.selectedMethod.cid, (m) => ({
            ...m,
            name: uniqueMethodName,
          }));
          return uniqueMethodName;
        }}
        isDisabled={props.isProcedureReadOnly}
      />
    </div>
  );
}

const borderStyle = `1px solid ${GRAY}`;

const styles = StyleSheet.create({
  metadataTabContent: {
    width: '50%',
  },
  methodNameField: {
    border: borderStyle,
  },
  methodNameFieldError: {
    border: `1px solid ${RED}`,
  },
});
